import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { ChangeDetectionStrategy, Component, EventEmitter, inject, input, linkedSignal } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { SelfServiceCommandKeys } from "../../../helpers/enums/selfServiceCommands";
import { GuidPipe } from "../../../helpers/guidpipe/guidpipe";
import { StartLoadMessage } from "../../../helpers/self-service/models/types";
import { ModifyL2VPNVlan } from "../../../helpers/self-service/modify_l2vpn";
import { SelfServiceCommand } from "../../../helpers/self-service/self-service-command";
import { isValidVlan } from "../../../helpers/self-service/vlanhelper";
import { getSortableId } from "../../../helpers/subscription/stringsort";
import { L2vpnSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { AuthService } from "../../../services/authservice";
import { SelfserviceWizard } from "../selfservice-wizard/selfservice-wizard";

@Component({
  selector: "selfservice-vlan-l2vpn",
  templateUrl: "selfservice-vlan-l2vpn.html",
  styleUrl: "../selfservice-add-port/selfservice-add-port.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, FormsModule, GuidPipe, SelfserviceWizard, TranslatePipe],
})
export class SelfserviceVlanL2vpn {
  protected auth = inject(AuthService);

  protected subscription = input.required<L2vpnSubscriptionDetails>();
  protected bus = input.required<EventEmitter<StartLoadMessage>>();

  protected esis = linkedSignal(() =>
    this.subscription().esis.map((esi) => {
      const endpoints = (esi.endpoints ?? []).map((endpoint) => {
        const usedVlans = (endpoint.port?.usedVlans ?? []).filter((v) => v !== endpoint.vlanrange);

        return {
          ...endpoint,
          port: { ...endpoint.port!, usedVlans },
          vlan: "",
        };
      });

      return {
        ...esi,
        vlan: esi.endpoints?.[0]?.vlanrange ?? "",
        endpoints,
        firstEndpoint: endpoints[0],
      };
    }),
  );

  protected usedVlansOnEsi(esiIndex: number) {
    const { endpoints } = this.esis()[esiIndex];
    const vlans = [...new Set(endpoints.flatMap((e) => e.port.usedVlans))];
    return vlans.sort((a, b) => getSortableId(a) - getSortableId(b));
  }

  protected validVlan(port: { usedVlans: string[] }, vlanString: string, currentVlan: string) {
    return vlanString === currentVlan || isValidVlan(port, vlanString);
  }

  protected vlanMatches() {
    const vlans = this.esis().map((esi) => esi.vlan);
    const hasMultiVlan = vlans.some((v) => /\D/.test(v));
    return !hasMultiVlan || new Set(vlans).size === 1;
  }

  protected readonly buildCommand = () => {
    const action = SelfServiceCommandKeys.ModifyL2VpnVlan;
    const modify = new ModifyL2VPNVlan();
    modify.subscription = this.subscription();

    const esis = this.esis()
      .filter((esi) => !esi.firewallInterconnect)
      .map((esi) =>
        esi.endpoints.map((endpoint) => ({
          vlan: !esi.vlan || esi.vlan === "" ? endpoint.vlanrange : esi.vlan,
          subscription_id: endpoint.port.subscriptionId,
        })),
      );

    modify.setEsis(esis);
    const command = new SelfServiceCommand(this.subscription().subscriptionId, action);
    command.payload = modify;
    return { command, action };
  };

  protected isMyPort(port: { customer?: { customerId: string } | null }) {
    return port.customer?.customerId === this.auth.viewingCustomer().customerId;
  }
}
